import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings, Plus, Power, Zap, Clock, Brain, X, Plus as PlusIcon, Bot,
  RefreshCw, ChevronDown, ChevronRight, Copy, Check, Eye,
  Activity, Database as DbIcon, FileText, MessageSquare, Server, User,
} from 'lucide-react';
import {
  registryApi, OrchestratorConfig, AssembledPrompt,
  memoriesApi, AgentMemory, tracesApi, ThreadSummary, SpanDetail,
  chatApi, ChatStatus,
} from '@/services/api/agentApi';
import { workflowApi } from '@/services/api/workflowApi';
import { VoiceAssignmentPanel } from './VoiceAssignmentPanel';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import * as errorUtils from '@/utils/errors';

// --- Constants ---

const statusColors: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-600',
  inactive: 'bg-gray-500/10 text-gray-500',
  maintenance: 'bg-amber-500/10 text-amber-600',
};

const priorityColors: Record<string, string> = {
  low: 'text-gray-500',
  medium: 'text-blue-500',
  high: 'text-amber-500',
  critical: 'text-red-500',
};

const memoryTypeColors: Record<string, string> = {
  fact: 'bg-emerald-500/10 text-emerald-600',
  hypothesis: 'bg-blue-500/10 text-blue-600',
  directive: 'bg-purple-500/10 text-purple-600',
  correction: 'bg-amber-500/10 text-amber-600',
};

const AGENT_ASPECT_MAP: Record<string, string[]> = {
  'Data Domain Agent': ['data-query', 'schema-mapping', 'field-selection', 'data-correction', 'time-preference'],
  'Workflow Domain Agent': ['workflow-creation', 'workflow-pattern'],
  'Device Domain Agent': ['device-control', 'device-discovery'],
  'Liaison Agent': ['routing', 'coordination'],
  'Org Knowledge Agent': ['knowledge', 'directive'],
  'Server Domain Agent': ['server', 'infrastructure'],
  'Research Domain Agent': ['research', 'web-search'],
};

const AUTO_REFRESH_MS = 30_000;

function getTriggerTopic(triggerConfig?: Record<string, unknown>): string | null {
  return typeof triggerConfig?.topic === 'string' ? triggerConfig.topic : null;
}

// --- Sub-components ---

function AgentCard({
  config,
  isRunning,
  isSelected,
  onClick,
}: {
  config: OrchestratorConfig;
  isRunning: boolean;
  isSelected: boolean;
  onClick: () => void;
}) {
  const isSystem = config.organization_id === 'default';
  const knowledgeCount = config.knowledge_entries?.length || 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left border rounded-lg p-4 space-y-3 transition-all hover:border-foreground/30 ${
        isSelected
          ? 'border-primary ring-1 ring-primary/30 bg-primary/5'
          : isSystem
            ? 'border-blue-500/20 bg-blue-500/[0.02]'
            : 'border-border'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium truncate">{config.name}</h3>
            <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
              isSystem ? 'bg-blue-500/10 text-blue-500' : 'bg-violet-500/10 text-violet-500'
            }`}>
              {isSystem ? 'System' : 'User'}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${statusColors[config.status] || ''}`}>
              {config.status}
            </span>
            {isRunning && (
              <span className="flex items-center gap-1 text-xs text-emerald-500">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                running
              </span>
            )}
          </div>
        </div>
        {knowledgeCount > 0 && (
          <span className="shrink-0 flex items-center gap-1 text-xs text-muted-foreground">
            <Brain className="h-3 w-3" />
            {knowledgeCount}
          </span>
        )}
      </div>

      {config.capabilities.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {config.capabilities.slice(0, 6).map((cap, i) => (
            <span key={i} className="text-xs bg-secondary px-2 py-0.5 rounded">{cap}</span>
          ))}
          {config.capabilities.length > 6 && (
            <span className="text-xs text-muted-foreground">+{config.capabilities.length - 6}</span>
          )}
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Zap className="h-3 w-3" />
          Tier {config.resource_preferences?.default_model_tier || 2}
        </span>
        <span className={`flex items-center gap-1 ${priorityColors[config.resource_preferences?.budget_priority || 'medium']}`}>
          <Clock className="h-3 w-3" />
          {config.resource_preferences?.budget_priority || 'medium'}
        </span>
      </div>
    </button>
  );
}

function PromptViewer({ configId }: { configId: string }) {
  const [prompt, setPrompt] = useState<AssembledPrompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    registryApi.getAssembledPrompt(configId)
      .then(r => setPrompt(r.data))
      .catch(() => setPrompt(null))
      .finally(() => setLoading(false));
  }, [configId]);

  const handleCopy = () => {
    if (prompt?.assembled) {
      navigator.clipboard.writeText(prompt.assembled);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground py-4">Loading prompt...</div>;
  if (!prompt) return <div className="text-sm text-muted-foreground py-4">Could not load prompt.</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">System Prompt</h4>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="text-xs bg-muted/50 border border-border rounded-lg p-4 overflow-y-auto whitespace-pre-wrap font-mono leading-relaxed">
        {prompt.system_prompt}
      </pre>

      {prompt.knowledge_entries.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">
            Knowledge Entries ({prompt.knowledge_entries.length})
          </h4>
          <div className="space-y-1">
            {prompt.knowledge_entries.map((entry, i) => (
              <div key={i} className="text-xs bg-muted/50 border border-border rounded px-3 py-2">
                {entry}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs text-muted-foreground border border-dashed border-border rounded-lg p-3 space-y-1">
        <p className="font-medium text-foreground/70">Prompt Assembly Order</p>
        <p>1. System prompt (shown above)</p>
        <p>2. Knowledge entries (appended as "Your Knowledge" section)</p>
        <p>3. Active skill instructions (if skill-driven request)</p>
        <p>4. System directives (from DIRECTIVE memories)</p>
        <p>5. Domain expertise (from FACT memories, weight &ge; 0.5)</p>
        <p>6. Known corrections (from CORRECTION memories)</p>
        <p>7. Recent observations (from HYPOTHESIS memories, weight &ge; 0.2)</p>
        <p>8. Chat history + current request</p>
      </div>
    </div>
  );
}

function AgentMemoryList({ agentName }: { agentName: string }) {
  const [memories, setMemories] = useState<AgentMemory[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const aspects = AGENT_ASPECT_MAP[agentName];
    const params: Record<string, string> = {
      origin: 'agent',
      limit: '50',
      sortBy: '-weight',
    };
    if (aspects?.length) params.aspects = aspects.join(',');

    memoriesApi.list(params)
      .then(r => setMemories(r.data || []))
      .catch(() => setMemories([]))
      .finally(() => setLoading(false));
  }, [agentName]);

  if (loading) return <div className="text-sm text-muted-foreground py-4">Loading memories...</div>;
  if (memories.length === 0) return <div className="text-sm text-muted-foreground py-4">No memories found for this agent.</div>;

  const filtered = typeFilter ? memories.filter(m => m.type === typeFilter) : memories;
  const types = Array.from(new Set(memories.map(m => m.type)));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setTypeFilter(null)}
          className={`text-xs px-2 py-1 rounded ${!typeFilter ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
        >
          All ({memories.length})
        </button>
        {types.map(type => (
          <button
            key={type}
            onClick={() => setTypeFilter(typeFilter === type ? null : type)}
            className={`text-xs px-2 py-1 rounded ${typeFilter === type ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
          >
            {type} ({memories.filter(m => m.type === type).length})
          </button>
        ))}
      </div>

      <div className="space-y-2 overflow-y-auto">
        {filtered.map(memory => (
          <div key={memory._id} className="border border-border rounded-lg p-3 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${memoryTypeColors[memory.type] || 'bg-gray-500/10 text-gray-500'}`}>
                {memory.type}
              </span>
              <span className="text-xs text-muted-foreground">
                weight: {(memory.weight * 100).toFixed(0)}%
              </span>
              {memory.use_count > 0 && (
                <span className="text-xs text-muted-foreground">
                  used {memory.use_count}x
                </span>
              )}
            </div>
            <p className="text-xs leading-relaxed">{memory.text}</p>
            {memory.aspects && memory.aspects.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {memory.aspects.map((a, i) => (
                  <span key={i} className="text-xs bg-secondary px-1.5 py-0.5 rounded">{a}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AgentTraceList({ agentName }: { agentName: string }) {
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedThread, setExpandedThread] = useState<string | null>(null);
  const [spans, setSpans] = useState<SpanDetail[]>([]);
  const [spansLoading, setSpansLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    tracesApi.listThreads({ agentRoute: agentName, limit: 20 })
      .then(r => setThreads(r.data?.threads || []))
      .catch(() => setThreads([]))
      .finally(() => setLoading(false));
  }, [agentName]);

  const toggleThread = async (threadId: string) => {
    if (expandedThread === threadId) {
      setExpandedThread(null);
      setSpans([]);
      return;
    }
    setExpandedThread(threadId);
    setSpansLoading(true);
    try {
      const r = await tracesApi.getSpans(threadId);
      setSpans(r.data || []);
    } catch {
      setSpans([]);
    } finally {
      setSpansLoading(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground py-4">Loading traces...</div>;
  if (threads.length === 0) return <div className="text-sm text-muted-foreground py-4">No recent traces for this agent.</div>;

  const statusIcon = (status: string) => {
    if (status === 'completed' || status === 'ok') return <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />;
    if (status === 'error') return <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />;
    return <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" />;
  };

  return (
    <div className="space-y-2 overflow-y-auto">
      {threads.map(thread => (
        <div key={thread.thread_id} className="border border-border rounded-lg">
          <button
            onClick={() => toggleThread(thread.thread_id)}
            className="w-full text-left p-3 flex items-center gap-3 hover:bg-muted/30"
          >
            {expandedThread === thread.thread_id
              ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
            {statusIcon(thread.status)}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{thread.title || thread.initiator_detail || thread.thread_id}</p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                <span>{thread.initiator}</span>
                <span>{thread.turn_count} turn{thread.turn_count !== 1 ? 's' : ''}</span>
                <span>{thread.total_duration_ms ? `${(thread.total_duration_ms / 1000).toFixed(1)}s` : ''}</span>
                {thread.total_tokens > 0 && <span>{thread.total_tokens.toLocaleString()} tokens</span>}
                <span>{new Date(thread.created_at).toLocaleString()}</span>
              </div>
            </div>
          </button>

          {expandedThread === thread.thread_id && (
            <div className="border-t border-border px-3 py-2 space-y-1.5 bg-muted/20">
              {spansLoading ? (
                <div className="text-xs text-muted-foreground py-2">Loading spans...</div>
              ) : spans.length === 0 ? (
                <div className="text-xs text-muted-foreground py-2">No spans recorded.</div>
              ) : (
                spans.map(span => (
                  <div key={span.span_id} className="flex items-start gap-2 text-xs">
                    {statusIcon(span.status)}
                    <div className="min-w-0">
                      <span className="font-medium">{span.kind}</span>
                      <span className="text-muted-foreground"> — {span.summary}</span>
                      {span.llm && (
                        <span className="text-muted-foreground"> ({span.llm.model}, {span.llm.tokens_input + span.llm.tokens_output} tokens)</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AgentConfigEditor({
  config,
  onSave,
}: {
  config: OrchestratorConfig;
  onSave: () => void;
}) {
  const [knowledgeEntries, setKnowledgeEntries] = useState<string[]>([...(config.knowledge_entries || [])]);
  const [newEntry, setNewEntry] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  useEffect(() => {
    setKnowledgeEntries([...(config.knowledge_entries || [])]);
    setNewEntry('');
    setSaved(false);
  }, [config._id]);

  const addEntry = () => {
    const trimmed = newEntry.trim();
    if (trimmed && !knowledgeEntries.includes(trimmed)) {
      setKnowledgeEntries([...knowledgeEntries, trimmed]);
      setNewEntry('');
    }
  };

  const removeEntry = (index: number) => {
    setKnowledgeEntries(knowledgeEntries.filter((_, i) => i !== index));
  };

  const saveKnowledge = async () => {
    setIsSaving(true);
    try {
      await registryApi.update(config._id, { knowledge_entries: knowledgeEntries });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSave();
    } catch {
      // error handled silently
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Resource Preferences (read-only display) */}
      <div>
        <h4 className="text-sm font-medium mb-3">Resource Preferences</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="border border-border rounded-lg p-3 text-center">
            <div className="text-lg font-semibold">{config.resource_preferences?.default_model_tier || 2}</div>
            <div className="text-xs text-muted-foreground">Model Tier</div>
          </div>
          <div className="border border-border rounded-lg p-3 text-center">
            <div className="text-lg font-semibold">{config.resource_preferences?.max_expert_agents || 0}</div>
            <div className="text-xs text-muted-foreground">Max Experts</div>
          </div>
          <div className="border border-border rounded-lg p-3 text-center">
            <div className={`text-lg font-semibold ${priorityColors[config.resource_preferences?.budget_priority || 'medium']}`}>
              {config.resource_preferences?.budget_priority || 'medium'}
            </div>
            <div className="text-xs text-muted-foreground">Budget Priority</div>
          </div>
        </div>
      </div>

      {/* MQTT Topics */}
      {config.mqtt_topics && (config.mqtt_topics.command || config.mqtt_topics.status || config.mqtt_topics.discovery) && (
        <div>
          <h4 className="text-sm font-medium mb-2">MQTT Topics</h4>
          <div className="space-y-1 text-xs font-mono">
            {config.mqtt_topics.command && (
              <div className="bg-muted/50 rounded px-2 py-1">
                <span className="text-muted-foreground">command:</span> {config.mqtt_topics.command}
              </div>
            )}
            {config.mqtt_topics.status && (
              <div className="bg-muted/50 rounded px-2 py-1">
                <span className="text-muted-foreground">status:</span> {config.mqtt_topics.status}
              </div>
            )}
            {config.mqtt_topics.discovery && (
              <div className="bg-muted/50 rounded px-2 py-1">
                <span className="text-muted-foreground">discovery:</span> {config.mqtt_topics.discovery}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Knowledge Entries (editable) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium">Knowledge Entries</h4>
          <button
            onClick={saveKnowledge}
            disabled={isSaving}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {saved ? <Check className="h-3 w-3" /> : null}
            {isSaving ? 'Saving...' : saved ? 'Saved' : 'Save Changes'}
          </button>
        </div>

        <div className="space-y-2">
          {knowledgeEntries.length === 0 && (
            <p className="text-xs text-muted-foreground italic">No knowledge entries. Add facts about what this agent knows.</p>
          )}
          {knowledgeEntries.map((entry, i) => (
            <div key={i} className="flex items-start gap-2 group">
              <span className="flex-1 text-xs bg-muted/50 border border-border rounded px-3 py-2">{entry}</span>
              <button
                onClick={() => removeEntry(i)}
                className="p-1 rounded hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
            </div>
          ))}

          <div className="flex gap-2">
            <input
              type="text"
              value={newEntry}
              onChange={e => setNewEntry(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addEntry()}
              placeholder="Add a knowledge entry..."
              className="flex-1 text-xs bg-background border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-ring"
            />
            <button
              onClick={addEntry}
              disabled={!newEntry.trim()}
              className="px-2 py-2 rounded-md bg-muted hover:bg-accent text-muted-foreground disabled:opacity-30"
            >
              <PlusIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Triggers */}
      {config.triggers.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">Triggers</h4>
          <div className="space-y-1">
            {config.triggers.map((trigger, i) => {
              const topic = getTriggerTopic(trigger.config);
              return (
                <div key={i} className="text-xs bg-muted/50 border border-border rounded px-3 py-2">
                  <span className="font-medium">{trigger.type}</span>
                  {topic && <span className="text-muted-foreground"> — {topic}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Deactivate */}
      <div className="pt-2 border-t border-border">
        {confirmDeactivate ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-destructive">Deactivate this agent?</span>
            <button
              onClick={async () => {
                try {
                  await registryApi.remove(config._id);
                  setConfirmDeactivate(false);
                  onSave();
                } catch { /* silent */ }
              }}
              className="text-xs text-destructive font-medium hover:text-destructive/80"
            >
              Yes, deactivate
            </button>
            <button
              onClick={() => setConfirmDeactivate(false)}
              className="text-xs text-muted-foreground"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmDeactivate(true)}
            className="text-xs text-destructive hover:text-destructive/80 flex items-center gap-1"
          >
            <Power className="h-3 w-3" />
            Deactivate Agent
          </button>
        )}
      </div>
    </div>
  );
}

// --- Detail Panel ---

type DetailTab = 'prompt' | 'memories' | 'traces' | 'config';

function AgentDetailPanel({
  config,
  onClose,
  onConfigSave,
}: {
  config: OrchestratorConfig;
  onClose: () => void;
  onConfigSave: () => void;
}) {
  const [activeTab, setActiveTab] = useState<DetailTab>('prompt');

  const tabs: { key: DetailTab; label: string; icon: React.ReactNode }[] = [
    { key: 'prompt', label: 'Prompt', icon: <FileText className="h-3.5 w-3.5" /> },
    { key: 'memories', label: 'Memories', icon: <Brain className="h-3.5 w-3.5" /> },
    { key: 'traces', label: 'Traces', icon: <Activity className="h-3.5 w-3.5" /> },
    { key: 'config', label: 'Config', icon: <Settings className="h-3.5 w-3.5" /> },
  ];

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b border-border">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold">{config.name}</h3>
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-colors ${
                  activeTab === tab.key
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-muted">
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      <div className="p-4">
        {activeTab === 'prompt' && <PromptViewer configId={config._id} />}
        {activeTab === 'memories' && <AgentMemoryList agentName={config.name} />}
        {activeTab === 'traces' && <AgentTraceList agentName={config.name} />}
        {activeTab === 'config' && <AgentConfigEditor config={config} onSave={onConfigSave} />}
      </div>
    </div>
  );
}

// --- Main Component ---

export const RegistryTab: React.FC = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<OrchestratorConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chatStatus, setChatStatus] = useState<ChatStatus | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<OrchestratorConfig | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Create Agent dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [agentForm, setAgentForm] = useState({
    name: '',
    description: '',
    intent: '',
    autonomy: 'guided' as 'supervised' | 'guided' | 'autonomous',
  });

  const loadData = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsLoading(true);
    else setIsRefreshing(true);

    try {
      const [registryResult, statusResult] = await Promise.all([
        registryApi.listAll(),
        chatApi.getStatus().catch(() => null),
      ]);
      setConfigs(registryResult.data || []);
      if (statusResult) setChatStatus(statusResult.data);
      setError(null);
    } catch (err: unknown) {
      setError(errorUtils.getErrorMessageOrFallback(err, 'Failed to load agents'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData(true);
    refreshTimerRef.current = setInterval(() => loadData(false), AUTO_REFRESH_MS);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [loadData]);

  const handleCreateAgent = async () => {
    if (!agentForm.name.trim()) return;
    setCreating(true);
    try {
      const result = await workflowApi.createWorkflow({
        name: agentForm.name.trim(),
        description: agentForm.description.trim() || undefined,
        status: 'draft',
        definition: { nodes: [], edges: [], triggers: [], variables: [] },
      });
      const workflowId = result.data?.id || result.data?._id;
      if (workflowId) {
        await workflowApi.patch(`/api/workflows/${workflowId}`, {
          type: 'agent',
          agentConfig: {
            autonomyLevel: agentForm.autonomy,
            originalIntent: agentForm.intent.trim() || agentForm.name.trim(),
            generation: 0,
            maxGenerations: 50,
            degradationPolicy: { fallbackDecision: 'alert', queueForLater: true },
            llmConfig: { routineModelTier: 3, escalationModelTier: 2, confidenceThreshold: 0.7 },
          },
        });
        setCreateOpen(false);
        setAgentForm({ name: '', description: '', intent: '', autonomy: 'guided' });
        navigate(`/workflow-editor/${workflowId}`);
      }
    } catch (err: unknown) {
      setError(errorUtils.getErrorMessageOrFallback(err, 'Failed to create agent workflow'));
    } finally {
      setCreating(false);
    }
  };

  const isAgentRunning = (name: string) => {
    return chatStatus?.orchestrators?.some(o => o.name === name) || false;
  };

  // Split into system and user agents
  const systemAgents = configs.filter(c => c.organization_id === 'default');
  const userAgents = configs.filter(c => c.organization_id !== 'default');
  const runningCount = configs.filter(c => isAgentRunning(c.name)).length;

  // Create Agent Dialog (preserved from original)
  const createAgentDialog = (
    <Dialog open={createOpen} onOpenChange={setCreateOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Create Agent Workflow
          </DialogTitle>
          <DialogDescription>
            Create a new workflow-based agent with deterministic execution and LLM reasoning on exceptions.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium mb-1 block">Name</label>
            <input
              type="text"
              value={agentForm.name}
              onChange={e => setAgentForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g., Pump Monitor Agent"
              className="w-full text-sm bg-background border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Description</label>
            <input
              type="text"
              value={agentForm.description}
              onChange={e => setAgentForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What does this agent do?"
              className="w-full text-sm bg-background border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Intent</label>
            <textarea
              value={agentForm.intent}
              onChange={e => setAgentForm(f => ({ ...f, intent: e.target.value }))}
              placeholder="Describe what this agent should accomplish..."
              rows={3}
              className="w-full text-sm bg-background border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-ring resize-none"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Autonomy Level</label>
            <select
              value={agentForm.autonomy}
              onChange={e => {
                const autonomy = e.target.value;
                if (
                  autonomy === 'supervised' ||
                  autonomy === 'guided' ||
                  autonomy === 'autonomous'
                ) {
                  setAgentForm((f) => ({ ...f, autonomy }));
                }
              }}
              className="w-full text-sm bg-background border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="supervised">Supervised — every change needs approval</option>
              <option value="guided">Guided — parameter changes auto-approved</option>
              <option value="autonomous">Autonomous — agent operates independently</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={() => setCreateOpen(false)}
            className="px-4 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateAgent}
            disabled={creating || !agentForm.name.trim()}
            className="px-4 py-2 rounded-md text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Create Agent'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-muted-foreground">Loading observatory...</div>
      </div>
    );
  }

  if (error && configs.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-destructive">{error}</p>
          <button onClick={() => { setError(null); loadData(true); }} className="text-sm text-primary hover:underline">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            Agent Observatory
          </h2>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Server className="h-3.5 w-3.5" />
              {systemAgents.length} system
            </span>
            <span className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              {userAgents.length} user
            </span>
            <span className="flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              {runningCount} running
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => loadData(false)}
            disabled={isRefreshing}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Agent
          </button>
        </div>
      </div>

      {/* System Agents Section */}
      {systemAgents.length > 0 && (
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
            <Server className="h-3.5 w-3.5" />
            System Agents ({systemAgents.length})
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
            {systemAgents.map(config => (
              <AgentCard
                key={config._id}
                config={config}
                isRunning={isAgentRunning(config.name)}
                isSelected={selectedAgent?._id === config._id}
                onClick={() => setSelectedAgent(selectedAgent?._id === config._id ? null : config)}
              />
            ))}
          </div>
        </div>
      )}

      {/* User Agents Section */}
      {userAgents.length > 0 && (
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
            <User className="h-3.5 w-3.5" />
            User Agents ({userAgents.length})
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
            {userAgents.map(config => (
              <AgentCard
                key={config._id}
                config={config}
                isRunning={isAgentRunning(config.name)}
                isSelected={selectedAgent?._id === config._id}
                onClick={() => setSelectedAgent(selectedAgent?._id === config._id ? null : config)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {configs.length === 0 && (
        <div className="text-center py-12">
          <Settings className="h-16 w-16 mx-auto text-muted-foreground/40" />
          <h3 className="text-lg font-medium text-muted-foreground mt-4">No Agents Found</h3>
          <p className="text-sm text-muted-foreground/70 mt-1">
            Start the agent service to see system agents here.
          </p>
        </div>
      )}

      {/* Detail Panel */}
      {selectedAgent && (
        <AgentDetailPanel
          config={selectedAgent}
          onClose={() => setSelectedAgent(null)}
          onConfigSave={() => loadData(false)}
        />
      )}

      {/* Voice Blend Panel */}
      {selectedAgent && (
        <VoiceAssignmentPanel
          orchestratorId={selectedAgent._id}
          currentBlend={selectedAgent.voice_config?.blend}
        />
      )}

      {createAgentDialog}
    </div>
  );
};
